import { action, observable, computed } from "mobx";
import NestStore from "~/stores/NestStore";
import Immersive3dTourRepository from "~/repository/Immersive3dTour/Immersive3dTourRepository";
import Immersive3dTour from "~/models/Immersive3dTour/Immersive3dTour";

class Immersive3dTourStore extends NestStore {
  /**
   * @property {Array}
   */
  @observable immersive3dTours = [];

  /**
   * @property {Immersive3dTour|null}
   */
  @observable immersive3dTour = null;

  /**
   * @property {number|null}
   */
  @observable tourId = null;

  @observable searchParams = {};

  /**
   * @property {Immersive3dTourRepository|null}
   */
  immersive3dTourRepository = null;

  /**
   * @property {string|null}
   */
  token = null;

  constructor() {
    super();

    this.immersive3dTourRepository = new Immersive3dTourRepository();

    this.setDefaultSearchParams();
  }

  @computed
  get hasSelectedFilters() {
    return this.searchParams.status !== "" || this.searchParams.keyword !== "";
  }

  @action
  load(tourId = null) {
    this.isLoading = true;

    if (tourId) {
      this.tourId = tourId;
    }

    return Promise.all([this.getOne()]).finally(() => {
      this.isLoading = false;
    });
  }

  @action
  loadIndex() {
    this.isLoading = true;

    return Promise.all([this.getAll()]).finally(() => {
      this.isLoading = false;
    });
  }

  /**
   * Get all.
   */
  @action
  getAll() {
    const params = {
      offset: this.offset,
      limit: this.pagination.itemsPerPage,
    };

    if (this.searchParams.providerId !== "") {
      params.providerId = this.searchParams.providerId;
    }

    if (this.searchParams.tourId !== "") {
      params.tourId = this.searchParams.tourId;
    }

    if (this.searchParams.status !== "") {
      params.status = this.searchParams.status;
    }

    if (this.searchParams.keyword !== "") {
      params.keyword = this.searchParams.keyword;
    }

    return this.immersive3dTourRepository.fetch(params).then(response => {
      this.processEntities(response.data.immersive3dTours);
      this.processPagination(response.data.pagination);
    });
  }

  @action
  processEntities(immersive3dTours = []) {
    this.immersive3dTours = [];

    immersive3dTours.forEach(immersive3dTourArray => {
      const immersive3dTour = new Immersive3dTour(immersive3dTourArray);

      this.immersive3dTours.push(immersive3dTour);
    });
  }

  @action
  getOne() {
    let query = null;

    if (this.token) {
      query = { token: this.token };
    }

    return this.immersive3dTourRepository.fetchOne(this.tourId, query).then(response => {
      this.immersive3dTour = new Immersive3dTour(response.data.immersive3dTour);
    });
  }

  /**
   * Change the pagination page and fetch the Photo Editing Jobs.
   */
  @action
  changePage(page) {
    this.pagination.currentPage = page;

    return this.loadIndex();
  }

  /**
   * Change the pagination page and fetch the Photo Editing Jobs.
   */
  @action
  search() {
    this.pagination.currentPage = 1;

    return this.loadIndex();
  }

  /**
   * Set default search params.
   */
  @action
  setDefaultSearchParams() {
    this.searchParams = observable({
      providerId: "",
      tourId: "",
      status: "",
      keyword: "",
    });
  }

  @action
  deliver(tourId) {
    this.isLoading = true;

    return this.immersive3dTourRepository
      .deliver(tourId)
      .then(() => this.getOne())
      .finally(() => {
        this.isLoading = false;
      });
  }

  @action
  getOneOrCreate(tourId) {
    if (tourId) {
      this.tourId = tourId;
    }

    return this.immersive3dTourRepository.fetchOrCreate(this.tourId).then(immersive3dTour => {
      this.immersive3dTour = new Immersive3dTour(immersive3dTour);
    });
  }

  @action
  update(tourId, data) {
    return this.immersive3dTourRepository.update(tourId, data).then(response => {
      this.immersive3dTour = new Immersive3dTour(response.data.immersive3dTour);
    });
  }

  @action
  process(tourId, data) {
    return this.immersive3dTourRepository.process(tourId, data).then(response => {
      this.immersive3dTour = new Immersive3dTour(response.data.immersive3dTour);
    });
  }

  @action
  delete(tourId) {
    this.isLoading = true;

    return this.immersive3dTourRepository.delete(tourId).finally(() => {
      this.isLoading = false;
    });
  }

  @action
  updateImageData(tourId, data) {
    return this.immersive3dTourRepository.updateImageData(tourId, data);
  }

  @action
  setNotes(notes) {
    this.immersive3dTour.notes = notes;
  }

  @action
  setPropertyType(propertyType) {
    this.immersive3dTour.propertyType = propertyType;
  }

  @action
  setPricingMethod(pricingMethod) {
    this.immersive3dTour.pricingMethod = pricingMethod;
  }

  @action
  processEntity(immersive3dTourData) {
    this.immersive3dTour = new Immersive3dTour(immersive3dTourData);
  }
}

export default Immersive3dTourStore;
